import { useMemo } from 'react';
import DOMPurify from 'dompurify';

interface RichContentProps {
  html: string | undefined | null;
  /** Tailwind className overrides for the prose wrapper. */
  className?: string;
}

/**
 * Allowlist mirrors api/src/shared/sanitize.ts. This is defense-in-depth:
 * the server already sanitizes on write, but we re-sanitize on every
 * render so historical data (or anything that ever bypassed the server
 * allowlist) cannot inject script content via dangerouslySetInnerHTML.
 */
const ALLOWED_TAGS = [
  'p', 'br', 'hr',
  'h2', 'h3', 'h4',
  'strong', 'em', 'u', 's', 'sub', 'sup',
  'code', 'pre',
  'blockquote',
  'ul', 'ol', 'li',
  'a',
  'img',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'figure', 'figcaption',
  'span', 'div',
];

const ALLOWED_ATTR = [
  'href', 'title', 'target', 'rel',
  'src', 'alt', 'width', 'height', 'loading',
  'colspan', 'rowspan',
  'class',
];

/**
 * Renders sanitized rich HTML produced by the admin RichEditor. Wraps the
 * output in Tailwind Typography's `prose` classes for sensible default
 * styling.
 */
export default function RichContent({ html, className = '' }: RichContentProps) {
  const clean = useMemo(() => {
    if (!html) return '';
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
      // sanitize-html on the server already enforces this; here we make
      // sure nothing slips through even if persisted data is older than
      // the current rules.
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|\/)/i,
    });
  }, [html]);

  if (!clean) return null;
  return (
    <div
      className={`prose prose-slate max-w-none ${className}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
